import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ShoppingCart, Plus, Minus, Trash2, Clock, CheckCircle2, 
  AlertTriangle, Send, User, Phone, UtensilsCrossed, Timer,
  ChefHat, Loader2
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SelfOrderPage() {
  const { sessionCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCart, setShowCart] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    loadMenu();
  }, [sessionCode]);

  // Countdown timer
  useEffect(() => {
    if (data?.session?.expires_in_seconds > 0) {
      setTimeRemaining(data.session.expires_in_seconds);
      const interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setError('Session expired. Please request a new QR code from staff.');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [data?.session?.expires_in_seconds]);

  const loadMenu = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/self-order/${sessionCode}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load menu. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.item_id === item.id);
      if (existing) {
        return prev.map(i => 
          i.item_id === item.id 
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, {
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: 1
      }];
    });
    toast.success(`Added ${item.name} to cart`);
  };

  const updateQuantity = (itemId, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.item_id === itemId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(i => i.item_id !== itemId));
  };

  const cartTotal = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const tax = subtotal * 0.18;
    return { subtotal, tax, total: subtotal + tax };
  }, [cart]);

  const filteredItems = useMemo(() => {
    if (!data?.menu_items) return [];
    if (selectedCategory === 'all') return data.menu_items;
    return data.menu_items.filter(item => item.category_id === selectedCategory);
  }, [data?.menu_items, selectedCategory]);

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      toast.error('Please add items to your cart');
      return;
    }

    if (data?.session?.auth_required) {
      if (!customerName.trim()) {
        toast.error('Please enter your name');
        return;
      }
      if (!customerPhone.trim()) {
        toast.error('Please enter your phone number');
        return;
      }
    }

    setSubmitting(true);
    try {
      const orderData = {
        customer_name: customerName.trim() || null,
        customer_phone: customerPhone.trim() || null,
        items: cart,
        notes: orderNotes.trim() || null
      };

      const res = await axios.post(`${API_URL}/api/self-order/${sessionCode}/place-order`, orderData);
      setOrderDetails(res.data);
      setOrderPlaced(true);
      setCart([]);
      toast.success('Order placed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Unable to Load Menu</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={loadMenu} className="bg-orange-500 hover:bg-orange-600">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-12 w-12 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Order Placed!</h2>
            <p className="text-gray-600 mb-4">Your order has been sent to the kitchen</p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">Order Number</p>
              <p className="text-2xl font-mono font-bold text-orange-600">
                #{orderDetails?.order_number}
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 text-gray-600 mb-4">
              <Timer className="h-5 w-5 text-orange-500" />
              <span>Estimated time: {orderDetails?.estimated_time}</span>
            </div>

            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <p className="text-sm text-orange-700">
                <ChefHat className="h-4 w-4 inline mr-1" />
                Our kitchen is preparing your order. Please wait at your table.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {data?.restaurant?.logo_url ? (
                <img 
                  src={data.restaurant.logo_url} 
                  alt={data.restaurant.name}
                  className="h-10 w-10 rounded-full object-cover"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <UtensilsCrossed className="h-5 w-5 text-orange-600" />
                </div>
              )}
              <div>
                <h1 className="font-bold text-gray-800">{data?.restaurant?.name}</h1>
                <p className="text-xs text-gray-500">Table {data?.session?.table_number}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`${timeRemaining < 300 ? 'border-red-300 text-red-600' : 'border-gray-300'}`}>
                <Clock className="h-3 w-3 mr-1" />
                {formatTime(timeRemaining)}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Category Tabs */}
      <div className="bg-white border-b sticky top-[60px] z-30">
        <div className="max-w-lg mx-auto">
          <ScrollArea className="w-full">
            <div className="flex gap-2 p-3">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                All
              </Button>
              {data?.categories?.map(cat => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={selectedCategory === cat.id ? 'bg-orange-500 hover:bg-orange-600' : ''}
                >
                  {cat.name}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Menu Items */}
      <main className="max-w-lg mx-auto px-4 py-4 pb-32">
        <div className="space-y-3">
          {filteredItems.map(item => {
            const cartItem = cart.find(c => c.item_id === item.id);
            return (
              <Card key={item.id} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex gap-3">
                    {item.image_url && (
                      <img 
                        src={item.image_url} 
                        alt={item.name}
                        className="h-20 w-20 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-gray-800 truncate">{item.name}</h3>
                          {item.description && (
                            <p className="text-xs text-gray-500 line-clamp-2">{item.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-bold text-orange-600">₹{item.price}</span>
                        
                        {cartItem ? (
                          <div className="flex items-center gap-2 bg-orange-50 rounded-full px-2 py-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 rounded-full"
                              onClick={() => updateQuantity(item.id, -1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center font-semibold">{cartItem.quantity}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 rounded-full"
                              onClick={() => updateQuantity(item.id, 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => addToCart(item)}
                            className="bg-orange-500 hover:bg-orange-600"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <UtensilsCrossed className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No items in this category</p>
            </div>
          )}
        </div>
      </main>

      {/* Cart Button */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
          <div className="max-w-lg mx-auto p-4">
            <Button 
              onClick={() => setShowCart(true)}
              className="w-full bg-orange-500 hover:bg-orange-600 h-14 text-lg"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              View Cart ({cart.reduce((sum, i) => sum + i.quantity, 0)} items)
              <span className="ml-auto font-bold">₹{cartTotal.total.toFixed(0)}</span>
            </Button>
          </div>
        </div>
      )}

      {/* Cart Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-md mx-auto max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-orange-500" />
              Your Order
            </DialogTitle>
            <DialogDescription>
              Table {data?.session?.table_number} • {data?.restaurant?.name}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 max-h-[40vh]">
            <div className="space-y-3 pr-4">
              {cart.map(item => (
                <div key={item.item_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-800">{item.item_name}</p>
                    <p className="text-sm text-gray-500">₹{item.price} × {item.quantity}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.item_id, -1)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-6 text-center font-semibold">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.item_id, 1)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-500"
                      onClick={() => removeFromCart(item.item_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Customer Info (if auth required) */}
          {data?.session?.auth_required && (
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm text-gray-600 font-medium">Your Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Your name"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone *</Label>
                  <Input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="Phone number"
                    className="h-9"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Order Notes */}
          <div className="border-t pt-4">
            <Label className="text-xs text-gray-600">Special Instructions</Label>
            <Input
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder="Any special requests? (optional)"
              className="h-9 mt-1"
            />
          </div>

          {/* Order Summary */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span>₹{cartTotal.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tax (18%)</span>
              <span>₹{cartTotal.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total</span>
              <span className="text-orange-600">₹{cartTotal.total.toFixed(2)}</span>
            </div>
          </div>

          <Button
            onClick={handlePlaceOrder}
            disabled={submitting || cart.length === 0}
            className="w-full bg-orange-500 hover:bg-orange-600 h-12 text-lg mt-4"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Placing Order...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Place Order
              </>
            )}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
